import React from 'react';
import { Artifact } from '../types';
import { X, QrCode } from 'lucide-react';

interface ArtifactModalProps {
  artifact: Artifact | null;
  onClose: () => void;
  onOpenScanner?: () => void;
}

export const ArtifactModal: React.FC<ArtifactModalProps> = ({ artifact, onClose, onOpenScanner }) => {
  if (!artifact) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-fade-in">
      <div 
        className="relative w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[#21171c] border border-[#9d7d49] rounded-2xl shadow-2xl grid grid-cols-1 md:grid-cols-[320px_1fr] overflow-hidden text-[#fff0dc]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="artifact-title"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-[#2d2026] hover:bg-[#3d2c34] text-[#fff0dc] border border-[#8e7148] flex items-center justify-center transition-all"
          aria-label="Đóng thông tin"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Left column: Stamp preview */}
        <div className={`p-8 flex flex-col items-center justify-center text-center bg-[#2c2025] border-b md:border-b-0 md:border-r border-[#665049] ${!artifact.unlocked ? 'opacity-90' : ''}`}>
          <div className="relative w-48 sm:w-56 aspect-[4/5] flex items-center justify-center transition-transform duration-300 hover:scale-105">
            <img
              src={artifact.stamp}
              alt={`Tem ${artifact.title}`}
              className={`w-full h-full object-contain filter drop-shadow-xl ${
                !artifact.unlocked ? 'grayscale-[70%] contrast-90 brightness-95' : ''
              }`}
            />
          </div>

          <div className="mt-4 px-3 py-1.5 rounded-xl bg-[#21171c] border border-[#685049] text-xs">
            {artifact.unlocked ? (
              <span className="text-[#5b9e8b] font-bold flex items-center gap-1 justify-center">
                ✓ Tem số đã lưu trong tài khoản
              </span>
            ) : (
              <span className="text-[#f0cf89] font-medium">
                Bản xem trước · Quét QR tại Bảo tàng để mở tem
              </span>
            )}
          </div>
        </div>

        {/* Right column: Details */}
        <div className="p-6 sm:p-8 flex flex-col justify-between space-y-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold tracking-widest text-[#e6ba55] uppercase">
                BỘ QUỐC BẢO · {artifact.unlocked ? 'ĐÃ NHẬN' : 'CHƯA MỞ KHÓA'}
              </span>
            </div>

            <h2 id="artifact-title" className="text-2xl sm:text-3xl font-serif font-bold text-white mt-2">
              {artifact.title}
            </h2>
            <p className="text-sm italic text-[#cdbdaf]">{artifact.en}</p>

            {/* Quick facts grid */}
            <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
              <div className="p-3 rounded-xl bg-[#2d2026] border border-[#685049]">
                <span className="text-[#cdbdaf] block">Tác giả</span>
                <strong className="text-white font-serif">{artifact.author}</strong>
              </div>
              <div className="p-3 rounded-xl bg-[#2d2026] border border-[#685049]">
                <span className="text-[#cdbdaf] block">Chất liệu / Thể loại</span>
                <strong className="text-white">{artifact.material}</strong>
              </div>
              <div className="p-3 rounded-xl bg-[#2d2026] border border-[#685049]">
                <span className="text-[#cdbdaf] block">Mã hiện vật</span>
                <strong className="font-mono text-[#e6ba55]">{artifact.code}</strong>
              </div>
              <div className="p-3 rounded-xl bg-[#2d2026] border border-[#685049]">
                <span className="text-[#cdbdaf] block">Vị trí trưng bày</span>
                <strong className="text-white">{artifact.location}</strong>
              </div>
            </div>

            {/* Story */}
            <div className="mt-5 space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[#e6ba55] block">
                CÂU CHUYỆN DI SẢN
              </span>
              <p className="text-sm text-[#cdbdaf] leading-relaxed">
                {artifact.story}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-4 border-t border-[#685049] flex flex-wrap items-center gap-3">
            {!artifact.unlocked && onOpenScanner && (
              <button
                onClick={() => {
                  onClose();
                  onOpenScanner();
                }}
                className="gf-button text-xs py-2.5 px-4 flex items-center gap-2"
              >
                <QrCode className="w-4 h-4" />
                <span>Mở máy ảnh quét mã</span>
              </button>
            )}

            <button
              onClick={onClose}
              className="gf-button gf-button-secondary text-xs py-2.5 px-4"
            >
              Đóng lại
            </button>

            <span className="text-xs text-[#cdbdaf] ml-auto">
              Nguồn tư liệu: {artifact.source}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
